/**
 * Optional extra server-side attributes for Meta events (e.g. client_pixel_fired).
 *
 * Kept separate from `commonFacebookEventsUtil` so the default dispatch stays
 * unchanged and this rollout can be reverted or extended per event name.
 *
 * `client_pixel_fired` is true only when the real FB SDK loaded (not stub-only)
 * and a facebook.com/tr (or connect.facebook.net) beacon was observed after track.
 */

import {
  getFacebookCommonAttributes,
  postFacebookMetaServerEvent,
  prepareFacebookMetaEventDispatch,
  removeEmptyParams,
  type CommonFacebookEventsOptions,
} from './facebookCommonAttributes';

const CLIENT_PIXEL_RETRY_MS = 1000;
/** Wait for facebook.com/tr (or SDK script) network activity after track. */
const CLIENT_PIXEL_BEACON_CONFIRM_MS = 500;

const FACEBOOK_TRACKING_URL_PATTERN = /facebook\.com\/tr|connect\.facebook\.net/i;

interface FbqFn {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
}

/** Stub-only pixel queues events; real SDK attaches callMethod once fbevents.js loads. */
function isFacebookPixelSdkLoaded(): boolean {
  const fbq = window.fbq as FbqFn | undefined;
  return typeof fbq === 'function' && typeof fbq.callMethod === 'function';
}

function countFacebookTrackingResources(): number {
  if (typeof performance === 'undefined') return 0;
  return performance
    .getEntriesByType('resource')
    .filter((entry) => FACEBOOK_TRACKING_URL_PATTERN.test(entry.name)).length;
}

function waitForNewFacebookTrackingResource(
  baselineCount: number,
  timeoutMs: number,
): Promise<boolean> {
  if (typeof performance === 'undefined') return Promise.resolve(false);

  return new Promise((resolve) => {
    let settled = false;
    let observer: PerformanceObserver | undefined;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const finish = (value: boolean) => {
      if (settled) return;
      settled = true;
      observer?.disconnect();
      if (intervalId != null) clearInterval(intervalId);
      if (timeoutId != null) clearTimeout(timeoutId);
      resolve(value);
    };

    const hasNewBeacon = () =>
      performance
        .getEntriesByType('resource')
        .filter((entry) => FACEBOOK_TRACKING_URL_PATTERN.test(entry.name)).length >
      baselineCount;

    if (hasNewBeacon()) {
      finish(true);
      return;
    }

    try {
      observer = new PerformanceObserver(() => {
        if (hasNewBeacon()) finish(true);
      });
      observer.observe({ type: 'resource', buffered: true });
    } catch {
      // PerformanceObserver unavailable — fall back to polling only.
    }

    intervalId = setInterval(() => {
      if (hasNewBeacon()) finish(true);
    }, 50);

    timeoutId = setTimeout(() => finish(false), timeoutMs);
  });
}

/** Add event names here to opt into server attribution extras. */
export const FACEBOOK_EVENTS_WITH_SERVER_ATTRIBUTION = new Set<string>([
  'ViewContent',
]);

export function isFacebookServerAttributionEnabled(eventName: string): boolean {
  return FACEBOOK_EVENTS_WITH_SERVER_ATTRIBUTION.has(eventName);
}

async function awaitFacebookClientPixel(
  eventName: string,
  eventAttributes: Record<string, unknown>,
  eventId: string,
): Promise<boolean> {
  const payload = removeEmptyParams({
    ...getFacebookCommonAttributes(),
    ...eventAttributes
  });

  let trackInvoked = false;
  let beaconBaseline: number | null = null;

  const invokeTrackOnce = (): boolean => {
    if (trackInvoked) return true;

    const fbq = window.fbq as FbqFn | undefined;
    if (typeof fbq !== 'function') return false;

    beaconBaseline = countFacebookTrackingResources();

    try {
      fbq('track', eventName, payload, { eventID: eventId });
      trackInvoked = true;
      console.log('client event triggered...', eventName, payload, { eventID: eventId });
      return true;
    } catch (error) {
      console.error(`Facebook Pixel track failed for '${eventName}':`, error);
      beaconBaseline = null;
      return false;
    }
  };

  /** Confirm SDK loaded + outbound beacon after the single track call. Never re-tracks. */
  const confirmDelivery = async (): Promise<boolean> => {
    if (!trackInvoked || beaconBaseline == null) return false;
    if (!isFacebookPixelSdkLoaded()) return false;
    return waitForNewFacebookTrackingResource(beaconBaseline, CLIENT_PIXEL_BEACON_CONFIRM_MS);
  };

  if (invokeTrackOnce()) {
    if (await confirmDelivery()) return true;
  }

  return new Promise((resolve) => {
    setTimeout(async () => {
      if (!trackInvoked) {
        invokeTrackOnce();
      }

      const confirmed = await confirmDelivery();
      if (!confirmed) {
        console.warn(`Facebook Pixel delivery unconfirmed for '${eventName}'.`);
      }
      resolve(confirmed);
    }, CLIENT_PIXEL_RETRY_MS);
  });
}

async function buildFacebookServerAttributionExtras(
  eventName: string,
  eventAttributes: Record<string, unknown>,
  eventId: string,
): Promise<Record<string, unknown>> {
  if (!isFacebookServerAttributionEnabled(eventName)) {
    return {};
  }

  const clientPixelFired = await awaitFacebookClientPixel(eventName, eventAttributes, eventId);
  return { 
    client_pixel_fired: clientPixelFired,
    variation: 'React',
    event_id: eventId,
  };
}

/**
 * Meta dispatch with optional server attribution extras.
 * Use instead of `commonFacebookEventsUtil` for opted-in events (ViewContent today).
 */
export function commonFacebookEventsUtilWithServerAttribution(
  eventName: string,
  eventAttributes: Record<string, unknown> = {},
  options?: CommonFacebookEventsOptions,
): void {
  const prepared = prepareFacebookMetaEventDispatch(eventName, eventAttributes);
  if (!prepared) return;

  void dispatchFacebookMetaEventWithServerAttribution(
    eventName,
    prepared.attributes,
    prepared.eventId,
    options,
  );
}

async function dispatchFacebookMetaEventWithServerAttribution(
  eventName: string,
  attributes: Record<string, unknown>,
  eventId: string,
  options?: CommonFacebookEventsOptions,
): Promise<void> {
  const serverExtras = await buildFacebookServerAttributionExtras(eventName, attributes, eventId);

  if (options?.skipServer) return;

  postFacebookMetaServerEvent(eventName, {
    ...attributes,
    ...serverExtras,
  });
}
